import { Update, type UpdateResult } from '@st/redux'
import { isMap, type JSONValue } from '@st/util/json-value'
import { Effect } from 'effect'
import { jsonMapPatch } from '@st/util/json-diff'
import { AnnotationPluginPolicy } from '../../annotation-plugin'
import {
  DocumentViewerMessage,
  DocumentViewerPluginMessage,
  DocumentViewerPluginStateModule
} from '../../document-viewer'
import {
  DocumentViewerCommandMessage,
  DocumentViewerPluginCommand
} from '../../document-viewer-command'
import { JsonStore, SessionJsonStore } from '../../json-store'
import type { DocumentLocation } from '../../page-layers'
import {
  AnnotationBase,
  AnnotationMetadata,
  AnnotationLocalMessage,
  AnnotationPluginState,
  AnnotationsSession,
  ToolSettings,
  ToolMessage,
  ToolStateBase,
  NewAnnotation
} from './types'

export const ANNOTATIONS_PLUGIN_ID = 'annotations' as const

export type AnnotationsPluginMessage = DocumentViewerPluginMessage<
  typeof ANNOTATIONS_PLUGIN_ID,
  AnnotationLocalMessage
>

export type AnnotationsPluginCommand = {
  type: 'create' | 'update' | 'delete'
  annotation: AnnotationBase
  documentId?: string
}

type AnnotationsHostCommand = DocumentViewerPluginCommand<
  typeof ANNOTATIONS_PLUGIN_ID,
  AnnotationsPluginCommand
>

export type AnnotationsPluginInit = {
  tools: AnnotationPluginPolicy<AnnotationBase, ToolStateBase>[]
  initialTool: string
  settingsKey: string
  storageNamespace: string
  createAnnotationMetadata: Effect.Effect<AnnotationMetadata, never, any>
  copyText: (text: string) => Effect.Effect<null, never, any>
}

export function makeAnnotationsPluginStateModule(
  init: AnnotationsPluginInit
): DocumentViewerPluginStateModule<AnnotationPluginState, AnnotationLocalMessage> {
  const { tools, initialTool, settingsKey, storageNamespace, createAnnotationMetadata, copyText } =
    init

  const toolStates = tools.reduce(
    (acc, plugin) => {
      acc[plugin.name] = { ...plugin.initialToolState }
      return acc
    },
    {} as Record<string, ToolStateBase>
  )

  const initialState: AnnotationPluginState = {
    annotations: {},
    annotationDrafts: {},
    selectionState: { type: 'none' },
    selectedToolName: initialTool,
    toolStates
  }

  return {
    init: ({ sessionKey }) =>
      Update.pipe(
        initialState,
        Update.effect(() =>
          loadPersistedAnnotationsPersistenceEffect(settingsKey, storageNamespace, sessionKey)
        )
      ),
    update: (state, message, { sessionKey, documentId }) => {
      if (message.type == 'sourceWillChange') {
        return flushActiveSelectionBeforeSourceChange(
          state,
          tools,
          settingsKey,
          storageNamespace,
          createAnnotationMetadata,
          copyText,
          sessionKey,
          documentId
        )
      }

      return applyAnnotationLocalMessage(
        state,
        message,
        tools,
        settingsKey,
        createAnnotationMetadata,
        copyText,
        sessionKey,
        documentId
      )
    }
  }
}

export function toAnnotationsPluginMessage(
  message: AnnotationLocalMessage
): AnnotationsPluginMessage {
  return { type: 'plugin', pluginId: ANNOTATIONS_PLUGIN_ID, message }
}

export function toViewerMessageFromAnnotationToolMessage(
  message: ToolMessage<AnnotationBase, ToolStateBase>
): DocumentViewerMessage {
  switch (message.type) {
    case 'setViewerZoom':
    case 'requestViewerScroll':
      return message
    default:
      return toAnnotationsPluginMessage(message)
  }
}

function reduceAnnotationPluginState(
  state: AnnotationPluginState,
  message: AnnotationLocalMessage,
  tools: AnnotationPluginPolicy<AnnotationBase, ToolStateBase>[],
  settingsKey: string,
  createAnnotationMetadata: Effect.Effect<AnnotationMetadata, never, any>,
  copyText: (text: string) => Effect.Effect<null, never, any>,
  sessionKey?: string,
  documentId?: string
): UpdateResult<AnnotationPluginState, DocumentViewerMessage> {
  switch (message.type) {
    case 'annotationsPersistenceLoaded':
      return applyLoadedAnnotationsPersistence(state, message.settings, message.session)
    case 'selectAnnotation': {
      const targetAnnotation = selAnnotationByIdInPluginState(state, message.annotationId)
      if (!targetAnnotation) {
        return state
      }

      return {
        ...state,
        selectionState: {
          type: 'selected',
          annotationId: message.annotationId
        }
      }
    }
    case 'selectAndScrollAnnotation': {
      const targetAnnotation = selAnnotationByIdInPluginState(state, message.annotationId)
      if (!targetAnnotation) return state

      return [
        {
          ...state,
          selectionState: {
            type: 'selected',
            annotationId: message.annotationId
          }
        },
        scrollToAnnotationEffect(targetAnnotation)
      ] as const
    }
    case 'deselectAnnotation':
      return { ...state, selectionState: { type: 'none' } }
    case 'copyTextRequested':
      return [state, copyText(message.text)]
    case 'beginEditingAnnotation':
      return {
        ...state,
        selectionState: {
          type: 'editing',
          annotationId: message.annotationDraft.id
        }
      }
    case 'requestAnnotationDraft':
      return [
        state,
        prepareAnnotationEffect(
          message.annotationDraft,
          tools,
          createAnnotationMetadata,
          (annotationDraft) => ({
            type: 'annotationDraftPrepared',
            annotationDraft,
            elementStatus: message.elementStatus,
            ...(sessionKey ? { sessionKey } : {})
          })
        )
      ] as const
    case 'annotationDraftPrepared':
      if (message.sessionKey != sessionKey) {
        return state
      }
      return {
        ...state,
        selectionState:
          message.elementStatus == 'selected' || message.elementStatus == 'editing'
            ? { type: message.elementStatus, annotationId: message.annotationDraft.id }
            : { type: 'none' },
        annotationDrafts: upsertAnnotationDraft(state.annotationDrafts, message.annotationDraft)
      }
    case 'createAnnotationDraft':
      return {
        ...state,
        selectionState:
          message.elementStatus == 'selected' || message.elementStatus == 'editing'
            ? { type: message.elementStatus, annotationId: message.annotationDraft.id }
            : { type: 'none' },
        annotationDrafts: upsertAnnotationDraft(state.annotationDrafts, message.annotationDraft)
      }
    case 'updateAnnotationDraft':
      return {
        ...state,
        annotationDrafts: upsertAnnotationDraft(state.annotationDrafts, message.annotationDraft)
      }
    case 'discardAnnotationDraft': {
      const nextDrafts = { ...state.annotationDrafts }
      delete nextDrafts[message.annotationId]

      const selectionState: AnnotationPluginState['selectionState'] =
        state.selectionState.type != 'none' &&
        state.selectionState.annotationId == message.annotationId
          ? { type: 'none' }
          : state.selectionState

      return { ...state, annotationDrafts: nextDrafts, selectionState }
    }
    case 'requestAnnotationCreation':
      return [
        state,
        prepareAnnotationEffect(
          message.annotationDraft,
          tools,
          createAnnotationMetadata,
          (annotationDraft) => ({
            type: 'annotationCreationPrepared',
            annotationDraft,
            ...(message.edit ? { edit: true } : {}),
            ...(sessionKey ? { sessionKey } : {})
          })
        )
      ] as const
    case 'annotationCreationPrepared': {
      if (message.sessionKey != sessionKey) {
        return state
      }
      const nextState = applyAnnotationDraft(state, message.annotationDraft)
      return [
        nextState,
        hostCommandEffect(annotationCreateCommand(message.annotationDraft, documentId))
      ] as const
    }
    case 'selectTool': {
      const selectedAnnotation = selActiveAnnotationInPluginState(state)
      const activePlugin = tools.find((plugin) => plugin.name == message.toolName)
      if (!activePlugin) {
        throw new Error(`Unknown annotation tool: ${message.toolName}`)
      }

      const shouldDeselect =
        selectedAnnotation && !activePlugin.handlesAnnotation?.(selectedAnnotation)

      const nextState: AnnotationPluginState = {
        ...state,
        selectedToolName: message.toolName,
        selectionState: shouldDeselect ? { type: 'none' } : state.selectionState
      }
      return [nextState, persistToolSettingsEffect(nextState, settingsKey)] as const
    }
    case 'updateTool': {
      const nextState: AnnotationPluginState = {
        ...state,
        toolStates: { ...state.toolStates, [message.tool.name]: message.tool }
      }
      return [nextState, persistToolSettingsEffect(nextState, settingsKey)] as const
    }
    case 'remoteAnnotationsUpdated':
      return reconcileAnnotationDraftsWithServerState({
        ...state,
        annotations: jsonMapPatch(state.annotations, message.changes)
      })
    case 'commitAnnotationDraft': {
      const annotationDraft = message.annotationDraft
      // Callers express one save intent. The state owner knows whether the ID
      // exists in the confirmed baseline and therefore owns create vs update;
      // exposing that distinction to views would duplicate workflow state.
      let command = annotationCreateCommand(annotationDraft, documentId)
      if (state.annotations[annotationDraft.id]) {
        command = annotationUpdateCommand(annotationDraft, documentId)
      }
      const nextState = applyAnnotationDraft(state, annotationDraft)

      // A save triggered by deselection must not reselect the old annotation.
      // Editors own any selection change that completes editing.
      return [nextState, hostCommandEffect(command)] as const
    }
    case 'deleteAnnotation': {
      const annotation = selAnnotationByIdInPluginState(state, message.annotationId)
      if (!annotation) return state

      // The host needs the annotation to apply its persistence policy. Capture it
      // before the deletion draft makes it disappear from viewer selectors.
      const nextState: AnnotationPluginState = {
        ...state,
        selectionState: { type: 'none' },
        annotationDrafts: { ...state.annotationDrafts, [message.annotationId]: null }
      }
      return [
        nextState,
        hostCommandEffect(annotationDeleteCommand(annotation, documentId))
      ] as const
    }
  }
}

function scrollToAnnotationEffect(annotation: AnnotationBase) {
  if (annotation.pageNumber == undefined) return Effect.void

  const target: DocumentLocation = {
    pageNumber: annotation.pageNumber,
    ...(annotation.bounds ? { bounds: annotation.bounds } : {})
  }

  return Effect.succeed({
    type: 'requestViewerScroll' as const,
    target,
    behavior: 'smooth' as const
  })
}

function applyAnnotationLocalMessage(
  state: AnnotationPluginState,
  message: AnnotationLocalMessage,
  tools: AnnotationPluginPolicy<AnnotationBase, ToolStateBase>[],
  settingsKey: string,
  createAnnotationMetadata: Effect.Effect<AnnotationMetadata, never, any>,
  copyText: (text: string) => Effect.Effect<null, never, any>,
  sessionKey?: string,
  documentId?: string
): UpdateResult<AnnotationPluginState, DocumentViewerMessage> {
  return Update.pipe(
    state,
    (nextState) =>
      reduceAnnotationPluginState(
        nextState,
        message,
        tools,
        settingsKey,
        createAnnotationMetadata,
        copyText,
        sessionKey,
        documentId
      ),
    (nextState) =>
      emitSelectionLifecycle({
        previousState: state,
        nextState,
        tools
      })
  )
}

function flushActiveSelectionBeforeSourceChange(
  state: AnnotationPluginState,
  tools: AnnotationPluginPolicy<AnnotationBase, ToolStateBase>[],
  settingsKey: string,
  storageNamespace: string,
  createAnnotationMetadata: Effect.Effect<AnnotationMetadata, never, any>,
  copyText: (text: string) => Effect.Effect<null, never, any>,
  sessionKey?: string,
  documentId?: string
): UpdateResult<AnnotationPluginState, DocumentViewerMessage> {
  const sessionSelectionState =
    state.selectionState.type == 'none' ? undefined : state.selectionState

  if (selActiveAnnotationIdInPluginState(state) == null) {
    return Update.pipe(
      state,
      Update.effect((nextState: AnnotationPluginState) =>
        persistAnnotationsSessionEffect(nextState, {
          storageNamespace,
          sessionKey,
          selectionState: sessionSelectionState
        })
      )
    )
  }

  const flushResult = collectSelectionLifecycleMessages({
    previousState: state,
    nextState: {
      ...state,
      selectionState: { type: 'none' }
    },
    tools
  }).reduce<UpdateResult<AnnotationPluginState, DocumentViewerMessage>>(
    (update, message) =>
      Update.pipe(update, (nextState) => {
        if (message.type == 'setViewerZoom' || message.type == 'requestViewerScroll') {
          return [
            nextState,
            Effect.succeed(toViewerMessageFromAnnotationToolMessage(message))
          ] as const
        }

        return applyAnnotationLocalMessage(
          nextState,
          message as AnnotationLocalMessage,
          tools,
          settingsKey,
          createAnnotationMetadata,
          copyText,
          sessionKey,
          documentId
        )
      }),
    {
      ...state,
      selectionState: { type: 'none' }
    }
  )

  return Update.pipe(
    flushResult,
    Update.effect((nextState: AnnotationPluginState) =>
      persistAnnotationsSessionEffect(nextState, {
        storageNamespace,
        sessionKey,
        selectionState: sessionSelectionState
      })
    )
  )
}

function emitSelectionLifecycle({
  previousState,
  nextState,
  tools
}: {
  previousState: AnnotationPluginState
  nextState: AnnotationPluginState
  tools: AnnotationPluginPolicy<AnnotationBase, ToolStateBase>[]
}): UpdateResult<AnnotationPluginState, DocumentViewerMessage> {
  const nextMessages = collectSelectionLifecycleMessages({
    previousState,
    nextState,
    tools
  })
  if (nextMessages.length == 0) return nextState
  return [
    nextState,
    Effect.succeed(nextMessages.map(toViewerMessageFromAnnotationToolMessage))
  ] as const
}

function collectSelectionLifecycleMessages({
  previousState,
  nextState,
  tools
}: {
  previousState: AnnotationPluginState
  nextState: AnnotationPluginState
  tools: AnnotationPluginPolicy<AnnotationBase, ToolStateBase>[]
}): ToolMessage<AnnotationBase, ToolStateBase>[] {
  const previousSelectedAnnotationId = selActiveAnnotationIdInPluginState(previousState)
  const nextSelectedAnnotationId = selActiveAnnotationIdInPluginState(nextState)

  if (previousSelectedAnnotationId == nextSelectedAnnotationId) {
    return []
  }

  const nextMessages: ToolMessage<AnnotationBase, ToolStateBase>[] = []

  if (previousSelectedAnnotationId) {
    const deselectedByDelete = nextState.annotationDrafts[previousSelectedAnnotationId] === null

    if (deselectedByDelete) {
      return nextMessages
    }

    const event = {
      type: 'deselectAnnotation' as const,
      annotation: previousState.annotations[previousSelectedAnnotationId],
      annotationDraft: previousState.annotationDrafts[previousSelectedAnnotationId]
    }
    const resolvedAnnotation = event.annotationDraft || event.annotation
    for (const plugin of tools) {
      if (plugin.name != resolvedAnnotation?.type) continue
      nextMessages.push(...runPluginLifecycleHook(plugin.onAnnotationDeselected, event))
    }
  }

  if (nextSelectedAnnotationId) {
    const event = {
      type: 'selectedAnnotation' as const,
      annotation: nextState.annotations[nextSelectedAnnotationId],
      annotationDraft: nextState.annotationDrafts[nextSelectedAnnotationId]
    }
    const resolvedAnnotation = event.annotationDraft || event.annotation
    for (const plugin of tools) {
      if (!resolvedAnnotation || !plugin.handlesAnnotation?.(resolvedAnnotation)) continue
      nextMessages.push(...runPluginLifecycleHook(plugin.onAnnotationSelected, event))
    }
  }

  return nextMessages
}

function runPluginLifecycleHook<Event extends { type: string }, Message extends { type: string }>(
  hook: ((event: Event) => Message | Message[] | null | undefined) | undefined,
  event: Event
): Message[] {
  const result = hook?.(event)
  return result == null ? [] : Array.isArray(result) ? result : [result as Message]
}

function hostCommandEffect(command: AnnotationsHostCommand) {
  return Effect.succeed({ type: 'viewerCommand', command } satisfies DocumentViewerCommandMessage)
}

function prepareAnnotationEffect(
  annotationDraft: NewAnnotation,
  tools: AnnotationPluginPolicy<AnnotationBase, ToolStateBase>[],
  createAnnotationMetadata: Effect.Effect<AnnotationMetadata, never, any>,
  preparedMessage: (annotationDraft: AnnotationBase) => AnnotationLocalMessage
) {
  return Effect.map(createAnnotationMetadata, (metadata) => {
    const annotation = {
      ...annotationDraft,
      id: metadata.id,
      time: metadata.time
    } as AnnotationBase
    const tool = tools.find((candidate) => candidate.handlesAnnotation?.(annotation))
    return toAnnotationsPluginMessage(
      preparedMessage(tool?.completeAnnotation?.(annotation, metadata) ?? annotation)
    )
  })
}

function persistToolSettingsEffect(state: AnnotationPluginState, settingsKey: string) {
  return Effect.gen(function* () {
    const settingsStore = yield* JsonStore
    yield* settingsStore.write(settingsKey, selToolSettingsInPluginState(state))
    return null
  })
}

function persistAnnotationsSessionEffect(
  state: AnnotationPluginState,
  {
    storageNamespace,
    sessionKey,
    selectionState
  }: {
    storageNamespace: string
    sessionKey?: string
    selectionState?: AnnotationsSession['selectionState']
  }
) {
  return Effect.gen(function* () {
    const annotationsSessionKey = resolveAnnotationsSessionStorageKey(storageNamespace, sessionKey)
    if (!annotationsSessionKey) {
      return null
    }

    const session =
      Object.keys(state.annotationDrafts).length == 0 && !selectionState
        ? null
        : {
            version: '2' as const,
            annotationDrafts: state.annotationDrafts,
            ...(selectionState ? { selectionState } : {})
          }
    const sessionStore = yield* SessionJsonStore
    if (!session) {
      yield* sessionStore.remove(annotationsSessionKey)
    } else {
      yield* sessionStore.write(annotationsSessionKey, session as Exclude<JSONValue, undefined>)
    }
    return null
  })
}

function loadPersistedAnnotationsPersistenceEffect(
  settingsKey: string,
  storageNamespace: string,
  sessionKey?: string
) {
  return Effect.gen(function* () {
    const settingsStore = yield* JsonStore
    const settings = parseToolSettings(yield* settingsStore.read(settingsKey))
    let session: AnnotationsSession | undefined

    const annotationsSessionKey = resolveAnnotationsSessionStorageKey(storageNamespace, sessionKey)
    if (annotationsSessionKey) {
      const sessionStore = yield* SessionJsonStore
      session = parseAnnotationsSession(yield* sessionStore.read(annotationsSessionKey))
    }

    if (!settings && !session) {
      return null
    }

    return toAnnotationsPluginMessage({
      type: 'annotationsPersistenceLoaded',
      ...(settings ? { settings } : {}),
      ...(session ? { session } : {})
    })
  })
}

function parseToolSettings(settings: JSONValue | undefined): ToolSettings | undefined {
  if (!isMap(settings) || settings.version != '1' || !isMap(settings.toolSettings)) {
    return undefined
  }

  if (settings.selectedToolName !== undefined && typeof settings.selectedToolName != 'string') {
    return undefined
  }

  for (const toolSettings of Object.values(settings.toolSettings)) {
    if (!isMap(toolSettings)) {
      return undefined
    }
  }

  return settings as ToolSettings
}

function parseAnnotationsSession(session: JSONValue | undefined): AnnotationsSession | undefined {
  if (!isMap(session) || session.version != '2' || !isMap(session.annotationDrafts)) {
    return undefined
  }

  for (const annotationDraft of Object.values(session.annotationDrafts)) {
    if (annotationDraft !== null && !isMap(annotationDraft)) {
      return undefined
    }
  }

  if (
    session.selectionState !== undefined &&
    !isPersistedSelectionState(session.selectionState as JSONValue | undefined)
  ) {
    return undefined
  }

  return session as AnnotationsSession
}

function upsertAnnotationDraft(
  annotationDrafts: AnnotationPluginState['annotationDrafts'],
  annotationDraft: AnnotationBase
) {
  return {
    ...annotationDrafts,
    [annotationDraft.id]: annotationDraft
  }
}

function applyAnnotationDraft(state: AnnotationPluginState, annotationDraft: AnnotationBase) {
  return {
    ...state,
    annotationDrafts: upsertAnnotationDraft(state.annotationDrafts, annotationDraft)
  }
}

function annotationCreateCommand(
  annotation: AnnotationBase,
  documentId?: string
): AnnotationsHostCommand {
  return {
    type: 'pluginCommand',
    pluginId: ANNOTATIONS_PLUGIN_ID,
    command: { type: 'create', annotation, ...(documentId ? { documentId } : {}) }
  }
}

function annotationUpdateCommand(
  annotation: AnnotationBase,
  documentId?: string
): AnnotationsHostCommand {
  return {
    type: 'pluginCommand',
    pluginId: ANNOTATIONS_PLUGIN_ID,
    command: { type: 'update', annotation, ...(documentId ? { documentId } : {}) }
  }
}

function annotationDeleteCommand(
  annotation: AnnotationBase,
  documentId?: string
): AnnotationsHostCommand {
  return {
    type: 'pluginCommand',
    pluginId: ANNOTATIONS_PLUGIN_ID,
    command: { type: 'delete', annotation, ...(documentId ? { documentId } : {}) }
  }
}

export function selAnnotationsPluginState(state: { pluginStates: Record<string, unknown> }) {
  return state.pluginStates[ANNOTATIONS_PLUGIN_ID] as AnnotationPluginState
}

function selActiveAnnotationIdInPluginState(state: AnnotationPluginState) {
  return state.selectionState.type == 'none' ? null : state.selectionState.annotationId
}

function selActiveAnnotationInPluginState(state: AnnotationPluginState) {
  const activeAnnotationId = selActiveAnnotationIdInPluginState(state)
  return activeAnnotationId ? selAnnotationByIdInPluginState(state, activeAnnotationId) : undefined
}

function selAnnotationByIdInPluginState(state: AnnotationPluginState, annotationId: string) {
  const draft = state.annotationDrafts[annotationId]
  if (draft === null) return undefined
  return draft ?? state.annotations[annotationId]
}

export function selActiveAnnotation(state: { pluginStates: Record<string, unknown> }) {
  return selActiveAnnotationInPluginState(selAnnotationsPluginState(state))
}

export function selAnnotationById(
  state: { pluginStates: Record<string, unknown> },
  annotationId: string
) {
  return selAnnotationByIdInPluginState(selAnnotationsPluginState(state), annotationId)
}

export function selAnnotationsForPage(
  state: { pluginStates: Record<string, unknown> },
  pageNumber: number
) {
  const pluginState = selAnnotationsPluginState(state)
  return Object.values({
    ...pluginState.annotations,
    ...pluginState.annotationDrafts
  }).filter((annot) => annot !== null && annot.pageNumber == pageNumber)
}

export function selAnnotations<T extends AnnotationBase>(
  state: { pluginStates: Record<string, unknown> },
  predicate?: (annotation: AnnotationBase) => annotation is T
): Record<string, T> {
  const pluginState = selAnnotationsPluginState(state)
  const entries = Object.entries({
    ...pluginState.annotations,
    ...pluginState.annotationDrafts
  }).filter(([_, annotation]) => annotation !== null) as [string, AnnotationBase][]
  return Object.fromEntries(
    predicate ? entries.filter(([_, annotation]) => predicate(annotation)) : entries
  ) as Record<string, T>
}

// A refreshed base may include another editor's work or an older save. Retire
// only intent it already satisfies; divergent local drafts remain the overlay.
// Drafts assert their defined keys, not omissions; a server-added border radius
// must not keep a saved rectangle draft alive. Arrays assert length and order.
// The kit's provisional time is metadata, not editable intent.
function reconcileAnnotationDraftsWithServerState(state: AnnotationPluginState) {
  const annotationDrafts = { ...state.annotationDrafts }
  for (const id of Object.keys(annotationDrafts)) {
    const annotation = state.annotations[id]
    const annotationDraft = annotationDrafts[id]

    if (annotationDraft === null && !annotation) {
      delete annotationDrafts[id]
    } else if (
      annotationDraft &&
      annotation &&
      satisfiesAnnotationDraft(
        annotation as JSONValue,
        { ...annotationDraft, time: undefined } as JSONValue
      )
    ) {
      delete annotationDrafts[id]
    }
  }
  return { ...state, annotationDrafts }
}

function satisfiesAnnotationDraft(value: JSONValue, draft: JSONValue): boolean {
  if (Array.isArray(draft)) {
    return (
      Array.isArray(value) &&
      value.length === draft.length &&
      draft.every((item, index) => satisfiesAnnotationDraft(value[index], item))
    )
  }
  if (isMap(draft)) {
    return (
      isMap(value) &&
      Object.entries(draft).every(
        ([key, item]) => item === undefined || satisfiesAnnotationDraft(value[key], item)
      )
    )
  }
  return value === draft
}

function selToolSettingsInPluginState(state: AnnotationPluginState): ToolSettings {
  return {
    version: '1',
    selectedToolName: state.selectedToolName,
    toolSettings: Object.fromEntries(
      Object.entries(state.toolStates).map(([name, ts]) => [name, ts.settings])
    )
  }
}

function applyLoadedAnnotationsPersistence(
  state: AnnotationPluginState,
  settings: ToolSettings | undefined,
  session: AnnotationsSession | undefined
): AnnotationPluginState {
  const toolStates = Object.fromEntries(
    Object.entries(state.toolStates).map(([toolName, toolState]) => [
      toolName,
      {
        ...toolState,
        settings: settings?.toolSettings[toolName] ?? toolState.settings
      }
    ])
  )

  const settingsSelectedToolName =
    settings?.selectedToolName && toolStates[settings.selectedToolName]
      ? settings.selectedToolName
      : state.selectedToolName

  const stateWithSettings: AnnotationPluginState = {
    ...state,
    selectedToolName: settingsSelectedToolName,
    toolStates
  }

  if (!session) {
    return stateWithSettings
  }

  const stateWithSession = reconcileAnnotationDraftsWithServerState({
    ...stateWithSettings,
    annotationDrafts: {
      ...stateWithSettings.annotationDrafts,
      ...session.annotationDrafts
    }
  })

  const selectionState =
    session.selectionState &&
    selAnnotationByIdInPluginState(stateWithSession, session.selectionState.annotationId)
      ? session.selectionState
      : { type: 'none' as const }

  return {
    ...stateWithSession,
    selectionState
  }
}

function isPersistedSelectionState(
  value: JSONValue | undefined
): value is AnnotationsSession['selectionState'] {
  return (
    isMap(value) &&
    (value.type == 'selected' || value.type == 'editing') &&
    typeof value.annotationId == 'string'
  )
}

function resolveAnnotationsSessionStorageKey(storageNamespace: string, sessionKey?: string) {
  return sessionKey ? `${storageNamespace}:${sessionKey}` : null
}
